import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './BusinessLogDirectoryTable.translations.json';
import {
  ColumnFiltersState,
  createColumnHelper,
  FilterFn,
  OnChangeFn,
} from '@tanstack/react-table';
import {
  BusinessLogDto,
  DirectoryTableProps,
  FilterOption,
  formatDateTimeShort,
  registerTableFilter,
  sortStringLower,
  SystemClassifierItemDto,
  Table,
  useSystemClassifierItemsBy,
} from '@atlas-frontend/sdk';
import { useCallback, useMemo } from 'react';
import { BusinessLogDataCell } from './BusinessLogDataCell';
import { LogDataFilterInput } from './filter/LogDataFilterInput';
import { mergeTraceIdFilter } from './filter/utils';

const { addTranslations, useTranslation } = i18nNsFactory(
  'business-log-directory-table',
);
addTranslations(translations);

const columnHelper = createColumnHelper<BusinessLogDto>();

export type BusinessLogDirectoryTableProps = DirectoryTableProps<any> & {
  setFilters: OnChangeFn<ColumnFiltersState>;
};

function categoriesToFilterOption(
  categories: Map<string, SystemClassifierItemDto>,
  extraOption?: FilterOption,
) {
  const filterOptions: FilterOption[] = [];
  for (const category of categories.values()) {
    filterOptions.push({
      id: category.code,
      name: category.name,
    });
  }
  if (extraOption) {
    filterOptions.push(extraOption);
  }
  filterOptions.sort((a, b) => sortStringLower(a.name, b.name));
  return filterOptions;
}

function dataKeysToFilterOption(
  logActions: Map<string, SystemClassifierItemDto>,
) {
  return categoriesToFilterOption(logActions, {
    id: 'freeText',
    name: 'Vabatekst',
  });
}

declare module '@tanstack/react-table' {
  interface FilterFns {
    logDataFilter: FilterFn<any>;
  }
}
registerTableFilter<BusinessLogDto>(
  'logDataFilter',
  () => true, // server side filtering
  LogDataFilterInput,
);

export function BusinessLogDirectoryTable(
  props: Readonly<BusinessLogDirectoryTableProps>,
) {
  const { setFilters } = props;
  const { t } = useTranslation();
  const {
    BUSINESS_LOG_ACTION: logActions,
    BUSINESS_LOG_DATA_KEY: dataKeys,
    BUSINESS_LOG_DATA2_KEY: data2Keys,
  } = useSystemClassifierItemsBy(
    ['BUSINESS_LOG_ACTION', 'BUSINESS_LOG_DATA_KEY', 'BUSINESS_LOG_DATA2_KEY'],
    'code',
  );

  const filterByTraceId = useCallback(
    (traceId: string) => {
      setFilters((filters) => {
        const logDataFilter = filters.find(({ id }) => id === 'logData');
        const logData = mergeTraceIdFilter(
          logDataFilter?.value as string | undefined,
          traceId,
        );

        return [
          ...filters.filter(({ id }) => id !== 'logData' && id !== 'traceId'),
          { id: 'logData', value: logData },
        ];
      });
    },
    [setFilters],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('createdAt', {
        header: t('column.createdAt'),
        cell: (props) => formatDateTimeShort(props.renderValue()) ?? '',
        filterFn: 'formattedDateTime',
        meta: {
          width: '20ch',
        },
      }),
      columnHelper.accessor('createdBy.username', {
        id: 'createdBy',
        header: t('column.user'),
        meta: {
          width: '20ch',
        },
      }),
      columnHelper.accessor(
        (rowData) => {
          const value = rowData.action;
          if (value) {
            return logActions.get(value)?.name ?? '';
          }
          return '';
        },
        {
          id: 'action',
          header: t('column.action'),
          filterFn: 'select',
          meta: {
            className: 'action',
            width: '25ch',
          },
        },
      ),
      columnHelper.accessor('logData', {
        header: t('column.logData'),
        cell: (props) => (
          <BusinessLogDataCell
            data={props.row.original}
            keys={dataKeys}
            logDataKey="logData"
            onTraceIdClick={filterByTraceId}
          />
        ),
        enableSorting: false,
        filterFn: 'logDataFilter',
        meta: {
          width: '40ch',
        },
      }),
      columnHelper.accessor('logData2', {
        header: t('column.logData2'),
        cell: (props) => (
          <BusinessLogDataCell
            data={props.row.original}
            keys={data2Keys}
            logDataKey="logData2"
            onTraceIdClick={filterByTraceId}
          />
        ),
        enableSorting: false,
        filterFn: 'logDataFilter',
        meta: {
          width: '40ch',
        },
      }),
    ],
    [t, logActions, dataKeys, filterByTraceId, data2Keys],
  );

  const options = useMemo(() => {
    return {
      action: categoriesToFilterOption(logActions),
      logData: dataKeysToFilterOption(dataKeys),
      logData2: dataKeysToFilterOption(data2Keys),
      userManaged: [
        { id: true, name: t('yes') },
        { id: false, name: t('no') },
      ],
    };
  }, [dataKeys, data2Keys, logActions, t]);

  return (
    <Table
      name="business-log-directory-table"
      columns={columns}
      options={options}
      hideClearFilters
      manualFiltering
      manualSorting
      scrollContainerSelector="#business-log-content"
      sticky
      virtual
      {...props}
    />
  );
}
